import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router-dom'

import { GearIcon } from '@phosphor-icons/react'

import { Card, Tabs } from '@reveni-io/reveni-ui'

import NestedRouteTitle from '@/common/components/NestedRouteTitle/NestedRouteTitle'
import TitlePage from '@/common/components/TitlePage/TitlePage'
import AppContext from '@/common/context/app.context'
import useMerchantFeatures from '@/common/hooks/useMerchantFeatures/useMerchantFeatures'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import useTabs from '@/common/hooks/useTabs/useTabs'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import { getDetailRouteInfo, getFieldKeysForTab } from '@/configuration/components/SettingsHistoryDrawer/helpers'
import SettingsHistoryDrawer from '@/configuration/components/SettingsHistoryDrawer/SettingsHistoryDrawer'

import useConfigurationPermissionRedirect from './hooks/useConfigurationPermissionRedirect/useConfigurationPermissionRedirect'
import { getTabs } from './helpers'
import * as Styles from './styles'

const ConfigurationPage = ({ isUnifiedDashboard }) => {
  const { t } = useTranslation()
  const { hasPermission, isOwner } = useUserPermissions()
  const { info } = useContext(AppContext)
  const location = useLocation()
  const { returnPortalFeatures } = useReturnPortalFeatures({
    returnPortalId: info?.selectedReturnPortal?.id,
    doInitialRequest: !isUnifiedDashboard,
  })
  const { merchantFeatures } = useMerchantFeatures({
    merchantId: info?.id,
    doInitialRequest: isUnifiedDashboard,
  })
  const features = isUnifiedDashboard ? merchantFeatures : returnPortalFeatures
  const [showHistory, setShowHistory] = useState(false)
  const tabs = getTabs({
    t,
    subscriptionPlan: info?.subscription_plan,
    hasPermission,
    isOwner,
    allowsShipments: info?.selectedStore?.preferences?.allows_shipments,
    isUnifiedDashboard,
    allowsEmailEditor: features?.email_editor_enabled,
    virtualTryonEnabled: features?.virtual_tryon_enabled,
    recommendationWidgetEnabled: features?.recommendation_widget_enabled,
    bracketingWidgetEnabled: features?.bracketing_widget_enabled,
    hasReturnProtection: info?.selectedStore?.return_protection?.regions?.length > 0,
  })
  const { tabSelected, onSelectTab } = useTabs({ tabs })

  useConfigurationPermissionRedirect({ tabs, returnPortalFeatures: features })

  const fieldKeys = getFieldKeysForTab(tabSelected, location.pathname)
  const { objectId, isCreation } = getDetailRouteInfo(tabSelected, location.pathname)
  const canOpenHistory = !isCreation && !isUnifiedDashboard && fieldKeys.length > 0

  const handleOpenHistory = () => setShowHistory(true)
  const handleCloseHistory = () => setShowHistory(false)

  return (
    <Styles.Container>
      <TitlePage Icon={GearIcon}>
        {isUnifiedDashboard
          ? t('Configuración global')
          : t('Configuración - {{storeName}}', { storeName: info?.selectedStore?.name })}
      </TitlePage>
      <div>
        <Styles.TabContainer>
          <Tabs elements={tabs} onChange={onSelectTab} selected={tabSelected} />
          <Styles.TabContent>
            <Card showBorder={false}>
              <NestedRouteTitle
                tabSelected={tabSelected}
                tabs={tabs}
                onOpenHistory={canOpenHistory && handleOpenHistory}
              />
              <Outlet />
            </Card>
          </Styles.TabContent>
        </Styles.TabContainer>
      </div>
      <SettingsHistoryDrawer
        show={showHistory && canOpenHistory}
        onClose={handleCloseHistory}
        fieldKeys={fieldKeys}
        returnPortalId={info?.selectedReturnPortal?.id}
        objectId={objectId}
      />
    </Styles.Container>
  )
}

export default ConfigurationPage
